import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { routes } from "@/configs/router.config/routes";
import { AgentMessage } from "@/models/agent/message";
import { AgentService } from "@/services/agent/agent.service";
import { useAgentStore } from "@/stores/agent/useAgentStore";

export const useSendMessage = () => {
  const { workspaceId, threadId } = useParams();
  const navigate = useNavigate();
  const [isSending, setIsSending] = useState(false);
  const pushOptimisticThread = useAgentStore((state) => state.pushOptimisticThread);
  const pushOptimisticMessage = useAgentStore((state) => state.pushOptimisticMessage);
  const setCurrentAgentRunId = useAgentStore((state) => state.setCurrentAgentRunId);

  // 新規チャット（threadIdなし）でAgent実行
  const handleSendNewMessage = (commandName?: string) => async (content: string) => {
    if (!workspaceId) return;

    try {
      setIsSending(true);
      const result = await AgentService.executeAgent({
        workspace_id: workspaceId,
        message: content,
        command: commandName,
      });
      setIsSending(false);

      // エージェント実行IDを保存
      setCurrentAgentRunId(result.agent_run_id);

      if (result.thread_id) {
        pushOptimisticThread(result.thread_id, workspaceId, content);
        navigate(routes.agentThread(workspaceId)(result.thread_id));
      }
    } catch (_error) {
      console.error("Failed to send message:", _error);
      toast.error("メッセージの送信に失敗しました。");
      setIsSending(false);
    }
  };

  const handleSendThreadMessage = (threadId: string, commandName?: string) => async (content: string) => {
    if (!workspaceId) return;

    // 楽観的ユーザーメッセージ追加
    const optimisticMessage = AgentMessage.createOptimisticMessage(threadId, content);
    pushOptimisticMessage(optimisticMessage);

    try {
      setIsSending(true);
      const result = await AgentService.executeAgent({
        workspace_id: workspaceId,
        message: content,
        thread_id: threadId,
        command: commandName,
      });
      setIsSending(false);

      // エージェント実行IDを保存
      setCurrentAgentRunId(result.agent_run_id);
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("メッセージの送信に失敗しました。");
      setIsSending(false);
    }
  };

  const handleSendMessage = async (content: string, commandName?: string) => {
    if (!workspaceId) return;

    const send = threadId
      ? handleSendThreadMessage(threadId, commandName)
      : handleSendNewMessage(commandName);
    await send(content);
  };

  return {
    handleSendMessage,
    isSending,
  };
};
